import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  Session,
  SessionStatus,
  ValidateSessionSuccessPayload,
} from "../../types/session.definition";
import { DEFAULT_PIN_LOCK_MINUTES } from "../../utils/pin-lock-utils";
import { RootState } from "../store";

interface SessionState {
  session: Session | null;
  expiresAt: string | null;
  loading: boolean;
  error: string | null;
  sessionId: string | null;
  sessionStatus: SessionStatus | null;
  sessionToken: string | null;
  language: string | null;
  features: Record<string, boolean>;
  /** null: the API does not send it (no PIN feature), the lock stays off. */
  hasPin: boolean | null;
  /** The server answered that the session is locked behind the PIN. */
  locked: boolean;
  /** The server ended the session (security code): shown before going to login. */
  endedReason: SessionEndedReason | null;
}

export type SessionEndedReason =
  | "PIN_CREATE_REAUTH_REQUIRED"
  | "SESSION_REUSE_DETECTED";

const applyPinState = (
  state: SessionState,
  payload: { hasPin?: boolean; locked?: boolean }
) => {
  if (typeof payload.hasPin === "boolean") state.hasPin = payload.hasPin;
  if (typeof payload.locked === "boolean") state.locked = payload.locked;
};

const initialState: SessionState = {
  session: null,
  expiresAt: null,
  loading: true,
  error: null,
  sessionId: null,
  sessionStatus: null,
  sessionToken: null,
  language: null,
  features: {},
  hasPin: null,
  locked: false,
  endedReason: null,
};

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    validateSessionRequest: state => {
      state.loading = true;
      state.error = null;
    },
    validateSessionSuccess: (
      state,
      action: PayloadAction<ValidateSessionSuccessPayload>
    ) => {
      state.error = null;
      state.sessionId = action.payload.sessionId;
      state.expiresAt = action.payload.expiresAt;
      state.sessionStatus = action.payload.sessionStatus;
      state.features = action.payload.settings?.features ?? {};
      applyPinState(state, action.payload);
    },
    validateSessionFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.sessionId = null;
      state.expiresAt = null;
      state.sessionStatus = SessionStatus.INVALID_SESSION;
    },
    getSessionRequest: state => {
      state.error = null;
    },
    getSessionSuccess: (state, action: PayloadAction<Session>) => {
      state.loading = false;
      state.session = action.payload;
      state.error = null;
      applyPinState(state, action.payload);
    },
    getSessionFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearSession: state => {
      state.session = null;
      state.loading = false;
      state.error = null;
      state.hasPin = null;
      state.locked = false;
    },
    /** 403 PIN_REQUIRED: the user has to create a PIN first. */
    pinRequired: state => {
      state.hasPin = false;
    },
    /** 423 SESSION_LOCKED: only a user with a PIN can be locked. */
    sessionLocked: state => {
      state.hasPin = true;
      state.locked = true;
    },
    /** PIN created, typed right or reset: the session is open again. */
    pinUnlocked: state => {
      state.hasPin = true;
      state.locked = false;
    },
    /** Lock delay saved from the settings: the timer uses it at once. */
    pinLockMinutesChanged: (state, action: PayloadAction<number>) => {
      if (state.session) state.session.pinLockMinutes = action.payload;
    },
    /** 403 PIN_CREATE_REAUTH_REQUIRED or 401 SESSION_REUSE_DETECTED. */
    sessionEnded: (state, action: PayloadAction<SessionEndedReason>) => {
      state.session = null;
      state.loading = false;
      state.sessionId = null;
      state.expiresAt = null;
      state.sessionStatus = SessionStatus.INVALID_SESSION;
      state.hasPin = null;
      state.locked = false;
      state.endedReason = action.payload;
    },
    requestTerminateSessionRequest: state => {
      state.loading = true;
    },
    setSessionToken: (state, action: PayloadAction<string>) => {
      state.sessionToken = action.payload;
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload;
    },
  },
});

export const {
  validateSessionRequest,
  validateSessionSuccess,
  validateSessionFailure,
  getSessionRequest,
  getSessionSuccess,
  getSessionFailure,
  clearSession,
  requestTerminateSessionRequest,
  setSessionToken,
  setLanguage,
  pinRequired,
  sessionLocked,
  pinUnlocked,
  pinLockMinutesChanged,
  sessionEnded,
} = sessionSlice.actions;

export default sessionSlice.reducer;
export const selectSessionStaticData = (state: RootState) => {
  return {
    sessionStatus: state.session.sessionStatus,
    expiresAt: state.session.expiresAt,
    sessionId: state.session.sessionId,
  };
};

export const selectSessionToken = (state: RootState) =>
  state.session.sessionToken;
export const selectLanguage = (state: RootState) => state.session.language;
export const selectFeatures = (state: RootState) =>
  state.session.features ?? {};
export const selectHasPin = (state: RootState) => state.session.hasPin ?? null;
export const selectSessionLocked = (state: RootState) =>
  state.session.locked ?? false;
export const selectSessionEndedReason = (state: RootState) =>
  state.session.endedReason ?? null;
/** Minutes without activity before the screen locks, 15 when not sent. */
export const selectPinLockMinutes = (state: RootState) =>
  state.session.session?.pinLockMinutes ?? DEFAULT_PIN_LOCK_MINUTES;
